import Box from '@material-ui/core/Box';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SidebarConfig, WbSidebarConfigContext } from '../config';
import { LocalStorage } from '../localStorage';
import { WbSidebarPinStateProvider } from '../WbSidebarPinStateContext';
import { AboutWitboostModalProvider } from '../components/AboutWitboostModal';

export type SidebarPageClassKey = 'root';

type StyleProps = { sidebarConfig: SidebarConfig; isPinned: boolean };

const useStyles = makeStyles<Theme, StyleProps>(
  theme => ({
    root: (props: StyleProps) => ({
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: '100%',
      transition: 'padding-left 0.1s ease-out',
      isolation: 'isolate',
      paddingLeft: props.isPinned
        ? props.sidebarConfig.drawerWidthOpen
        : props.sidebarConfig.drawerWidthClosed,
      '@media print': {
        padding: '0px !important',
      },
      [theme.breakpoints.down(props.sidebarConfig.mobileBreakpoint)]: {
        paddingLeft: '0px',
      },
    }),
    content: {
      zIndex: 0,
      isolation: 'isolate',
      '&:focus': {
        outline: 0,
      },
    },
  }),
  { name: 'WitboostSidebarPage' },
);

/**
 * Props for SidebarPage
 *
 * @public
 */
export type SidebarPageProps = {
  defaultPinState?: boolean;
  children?: ReactNode;
};

type PageContextType = {
  content: {
    contentRef?: MutableRefObject<HTMLElement | null>;
  };
};

const PageContext = createContext<PageContextType>({
  content: {
    contentRef: undefined,
  },
});
export function WbSidebarPage(props: SidebarPageProps) {
  const [isPinned, setIsPinned] = useState(() =>
    LocalStorage.getSidebarPinState(props.defaultPinState),
  );

  const [temporarilyPinned, setTemporarilyPinned] = useState(false);

  const { sidebarConfig } = useContext(WbSidebarConfigContext);

  const contentRef = useRef(null);

  const pageContext = useMemo(
    () => ({
      content: {
        contentRef,
      },
    }),
    [contentRef],
  );

  useEffect(() => {
    LocalStorage.setSidebarPinState(isPinned);
  }, [isPinned]);

  const toggleSidebarPinState = () => setIsPinned(!isPinned);

  const classes = useStyles({
    isPinned: isPinned || temporarilyPinned,
    sidebarConfig,
  });

  return (
    <AboutWitboostModalProvider>
      <WbSidebarPinStateProvider
        value={{
          isPinned: isPinned || temporarilyPinned,
          toggleSidebarPinState,
          isTemporarilyPinned: temporarilyPinned,
          setTemporarilyPinned,
        }}
      >
        <PageContext.Provider value={pageContext}>
          <Box className={classes.root}>{props.children}</Box>
        </PageContext.Provider>
      </WbSidebarPinStateProvider>
    </AboutWitboostModalProvider>
  );
}

/**
 * This hook provides a react ref to the main content.
 * Allows to set an element as the main content and focus on that component.
 *
 * *Note: If `contentRef` is not set `focusContent` is noop. `Content` component sets this ref automatically*
 *
 * @public
 * @example
 * Focus current content
 * ```tsx
 *  const { focusContent} = useContent();
 * ...
 *  <Button onClick={focusContent}>
 *     focus main content
 *  </Button>
 * ```
 * @example
 * Set the reference to an Html element
 * ```
 *  const { contentRef } = useContent();
 * ...
 *  <article ref={contentRef} tabIndex={-1}>Main Content</article>
 * ```
 */
export function useContent() {
  const { content } = useContext(PageContext);

  const focusContent = useCallback(() => {
    content?.contentRef?.current?.focus();
  }, [content]);

  return { focusContent, contentRef: content?.contentRef };
}
